import type { AnimationControls, Variants } from 'framer-motion';
import { motion } from 'framer-motion';

import { type AnimatedIconHandle, withAnimatedIcon } from './withAnimatedIcon';

type FlaskConicalIconRenderProps = {
  controls: AnimationControls;
  size: number;
};

// Decaying shake — vigorous at first, settles toward calm.
// Pivot is the top-center of the neck (where you'd grip the flask).
const shakeVariants: Variants = {
  normal: {
    rotate: 0,
    transition: { duration: 0.25, ease: 'easeOut' },
  },
  animate: {
    rotate: [0, -8, 7, -6, 5, -3, 0],
    transition: {
      duration: 0.95,
      ease: 'easeInOut',
      times: [0, 0.14, 0.3, 0.46, 0.62, 0.82, 1],
    },
  },
};

// Bubbles nucleate in the liquid, rise toward the surface, and pop.
// Staggered so the chain reads as continuous agitation, and the last bubble
// lingers slightly past the end of the shake — the liquid is still settling.
const bubbleVariants: Variants = {
  normal: { opacity: 0, y: 0, scale: 0 },
  animate: (custom: number) => ({
    opacity: [0, 1, 1, 0],
    y: [0, -1, -2, -3],
    scale: [0, 1, 1, 0],
    transition: {
      duration: 0.7,
      delay: 0.15 + custom * 0.2,
      ease: [0.4, 0, 0.2, 1],
      times: [0, 0.25, 0.7, 1],
    },
  }),
};

const FlaskConicalIconRender = ({ controls, size }: FlaskConicalIconRenderProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <motion.g
      variants={shakeVariants}
      animate={controls}
      style={{ transformOrigin: '50% 0%', transformBox: 'fill-box' }}
    >
      <path d="M10 2v7.527a2 2 0 0 1-.211.896L4.72 20.55a1 1 0 0 0 .9 1.45h12.76a1 1 0 0 0 .9-1.45l-5.069-10.127A2 2 0 0 1 14 9.527V2" />
      <path d="M8.5 2h7" />
      <path d="M7 16h10" />
      <motion.circle
        cx="10"
        cy="19"
        r="0.7"
        fill="currentColor"
        stroke="none"
        variants={bubbleVariants}
        animate={controls}
        custom={0}
        style={{ transformOrigin: '50% 50%', transformBox: 'fill-box' }}
      />
      <motion.circle
        cx="13"
        cy="19.5"
        r="0.55"
        fill="currentColor"
        stroke="none"
        variants={bubbleVariants}
        animate={controls}
        custom={1}
        style={{ transformOrigin: '50% 50%', transformBox: 'fill-box' }}
      />
      <motion.circle
        cx="11.5"
        cy="20"
        r="0.5"
        fill="currentColor"
        stroke="none"
        variants={bubbleVariants}
        animate={controls}
        custom={2}
        style={{ transformOrigin: '50% 50%', transformBox: 'fill-box' }}
      />
    </motion.g>
  </svg>
);

export type FlaskConicalIconHandle = AnimatedIconHandle;

export const FlaskConicalIcon = withAnimatedIcon(FlaskConicalIconRender, 'FlaskConicalIcon');
